import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RealtimeKitProvider } from "@cloudflare/realtimekit-react";
import { RtkMeeting } from "@cloudflare/realtimekit-react-ui";
import { initRealtimeKitMeeting } from "../../utils/realtimeKitInit";

const maskToken = (token) => {
  if (!token) return "(none)";
  return `${token.slice(0, 12)}…${token.slice(-8)} (len=${token.length})`;
};

const INIT_TIMEOUT_MS = 20000;

/**
 * Isolated RealtimeKit visual/API test page (not the production HIDOC flow).
 * Pass participant authToken via URL query only — never hardcode.
 * Example: /realtimekit-test?authToken=<token>
 */
const RealtimeKitTestPage = () => {
  const [searchParams] = useSearchParams();
  const authToken = searchParams.get("authToken");
  const [meeting, setMeeting] = useState(null);
  const [initError, setInitError] = useState(null);
  const [initStatus, setInitStatus] = useState("idle");

  useEffect(() => {
    if (!authToken) return;

    let cancelled = false;
    setMeeting(null);
    setInitError(null);
    setInitStatus("initializing");

    console.info(
      "[RealtimeKitTest] URL authToken read:",
      maskToken(authToken)
    );

    initRealtimeKitMeeting(authToken, { audio: true, video: true })
      .then((client) => {
        if (cancelled) return;
        if (!client) {
          setInitError("RealtimeKitClient.init resolved with undefined client");
          setInitStatus("failed");
          return;
        }
        console.info("[RealtimeKitTest] init OK", {
          meetingId: client?.meta?.meetingId,
          name: client?.self?.name,
        });
        setMeeting(client);
        setInitStatus("ready");
      })
      .catch((error) => {
        if (cancelled) return;
        const message =
          error?.message ||
          error?.error?.message ||
          error?.toString?.() ||
          "Unknown RealtimeKitClient.init error";
        const code = error?.code || error?.error?.code || null;
        const details = code ? `${code}: ${message}` : message;
        console.error("[RealtimeKitTest] init rejected", error);
        setInitError(details);
        setInitStatus("failed");
      });

    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      setInitStatus((status) => {
        if (status === "ready") return status;
        setInitError(
          (prev) =>
            prev ||
            `Timed out after ${INIT_TIMEOUT_MS}ms waiting for RealtimeKitClient.init`
        );
        return "failed";
      });
    }, INIT_TIMEOUT_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [authToken]);

  if (!authToken) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          padding: "24px",
          textAlign: "center",
        }}
      >
        <div>
          <h2>RealtimeKit visual test</h2>
          <p>
            Provide a participant authToken via the URL query parameter.
          </p>
          <code>/realtimekit-test?authToken=&lt;participant_authToken&gt;</code>
        </div>
      </div>
    );
  }

  if (initError && !meeting) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          padding: "24px",
          textAlign: "center",
        }}
      >
        <div>
          <h2>RealtimeKit init failed</h2>
          <p style={{ color: "#b00020", whiteSpace: "pre-wrap" }}>{initError}</p>
          <p>Token: {maskToken(authToken)}</p>
          <p>Status: {initStatus}</p>
        </div>
      </div>
    );
  }

  return (
    <RealtimeKitProvider
      value={meeting}
      fallback={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            gap: "8px",
          }}
        >
          <h2>Loading RealtimeKit…</h2>
          <p>Status: {initStatus}</p>
          <p>Token: {maskToken(authToken)}</p>
        </div>
      }
    >
      <div style={{ height: "100vh" }}>
        <RtkMeeting mode="fill" meeting={meeting} showSetupScreen={true} />
      </div>
    </RealtimeKitProvider>
  );
};

export default RealtimeKitTestPage;
